import { UserRegionEnum } from '@ad/ad_utils/src/businessRelated/ttcx_brief/type';
import {
  AddOnCategory,
  AddOnOfficialPriceTypeEnum,
  AddOnTypeEnum,
  ServicePackageRateCardInfo,
  StarlingText
} from '@ad/ad_utils_react/src/components/business/TTCX/AddOn/types';
import { UserRoleEnum } from '@ad/creative-business-components/src/components/Insights/types';
import { AddOnTableTitleMap } from '@/containers/BusinessRelatedParams/consts';
import { TFunction, t } from 'i18next';
import { formatCurrency } from '@ad/ad_utils_react/src/components/business/TTCX/AddOn/helper';
import { Trans } from 'react-i18next';
import './index.module.scss';
import React from 'react';
import { UserRegionEnumMap } from '@/containers/BusinessRelatedParams/typings';
import DOMPurify from 'dompurify';

const NARevShareTableData = {
  tiers: [
    {
      name: 'Tier 1',
      revShareTotalAmount: '$0-4,000',
      revSharePercentage: '10%'
    },
    {
      name: 'Tier 2',
      revShareTotalAmount: '$4,000-10,000',
      revSharePercentage: '8%'
    },
    {
      name: 'Tier 3',
      revShareTotalAmount: '>$10,000',
      revSharePercentage: '6%'
    }
  ]
};

function convertT(
  tFunc: TFunction<'translation', undefined>,
  content: StarlingText | undefined,
  isStyle = false
) {
  return isStyle ? (
    <Trans
      t={tFunc}
      i18nKey={content?.starlingKey}
      defaults={content?.fallbackText}
      components={{
        br: <br />,
        b: <b />
      }}
    />
  ) : (
    tFunc(content?.starlingKey || 'error', content?.fallbackText)
  );
}

function RevShareInformationTable() {
  return (
    <div className="rev-share-info-table">
      <div>Revenue Share</div>
      <div className="packages-table-content">
        <table>
          <tbody>
            <tr>
              <td>Tier</td>
              <td>{'Ad Revenue per Creative (within 60 days since the campaign launch)'}</td>
              <td>Revenue Share</td>
            </tr>
            {NARevShareTableData.tiers.map((tier) => (
              <tr key={tier.name}>
                <td>{tier.name}</td>
                <td>{tier.revShareTotalAmount}</td>
                <td>{tier.revSharePercentage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <br />
      <div>
        <p>
          Revenue share is based on stacked tiering, using one order as a unit. Maximum payment per video:
          $50,000
        </p>
        <p>Example Scenario: </p>
        <p>
          If an order has produced <b>8 videos</b>, and the average ad revenue per video is $5000. Then the{' '}
          <b>payment per video</b> is $880 =
          <ul>
            <li style={{ listStyleType: 'initial' }}>Base price: $400</li>
            <li style={{ listStyleType: 'initial' }}>+ Rev share in tier 1: $4000*10% = $400</li>
            <li style={{ listStyleType: 'initial' }}>+ Rev share in tier 2: ($5000-$4000)*8% = $80</li>
          </ul>
        </p>
        <p>
          <b>Total payment for the order = $880*8 = $7,040</b>
        </p>
      </div>
    </div>
  );
}

function RevShareAddOn() {
  return (
    <>
      <div className="packages-table-addOn">Revenue Share</div>
      <p style={{ marginLeft: '8px' }}>
        Standard revenue share terms apply. Any additional assets delivered for a project will be eligible for
        base pay + revenue share.
      </p>
    </>
  );
}

function VCPTableSchema(props: {
  region: UserRegionEnum;
  showRegion?: boolean;
  userRole?: UserRoleEnum;
  netNewPackages?: ServicePackageRateCardInfo[];
  repurposePackages?: ServicePackageRateCardInfo[];
  addOns?: AddOnCategory[];
  regionTextMap: UserRegionEnumMap;
}) {
  const {
    region,
    showRegion = true,
    userRole,
    netNewPackages = [],
    repurposePackages = [],
    addOns,
    regionTextMap
  } = props;

  const flatRateNetNew = netNewPackages.filter((pack) => !!pack); // Support future filtering
  const revShareNetNew = netNewPackages.filter((pack) => !!pack.basePayPerVideo);
  const flatRateRepurpose = repurposePackages.filter((pack) => !!pack); // Support future filtering
  const revShareRepurpose = repurposePackages.filter((pack) => !!pack.basePayPerVideo);

  return (
    <>
      {showRegion && ((flatRateNetNew?.length ?? 0) >= 1 || (flatRateRepurpose?.length ?? 0) >= 1) && (
        <div className="packages-table-region">
          {t(`ttcx_region_${regionTextMap[region]}`) + ' (VCP) - Flat Fee Structure'}
        </div>
      )}
      {((flatRateNetNew?.length ?? 0) >= 1 || (flatRateRepurpose?.length ?? 0) >= 1) && (
        <>
          <div className="packages-table-content">
            <table>
              <tbody>
                <tr>
                  <td>{t('exhibit_rate_card_header_service_type', 'Service Type')}</td>
                  {flatRateNetNew.length > 0 && (
                    <td colSpan={flatRateNetNew.length} key={'net-new'}>
                      {t('net_new', 'Net-New')}
                    </td>
                  )}
                  {flatRateRepurpose.length > 0 && (
                    <td colSpan={flatRateRepurpose.length} key={'repurpose'}>
                      {t('remix_project_title', 'Remix')}
                    </td>
                  )}
                </tr>
                <tr>
                  <td>Rate</td>
                  {flatRateNetNew?.map((item) => (
                    <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
                  ))}
                  {flatRateRepurpose?.map((item) => (
                    <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_price', 'Price')}</td>
                  {flatRateNetNew?.map((item) => (
                    <td key={item?.price}>
                      {`${(item?.price || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: item?.currency || 'USD',
                        minimumFractionDigits: 0
                      })} ${item?.currency || 'USD'}`}
                    </td>
                  ))}
                  {flatRateRepurpose?.map((item) => (
                    <td key={item?.price}>
                      {`${(item?.price || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: item?.currency || 'USD',
                        minimumFractionDigits: 0
                      })} ${item?.currency || 'USD'}`}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_assets', 'Assets')}</td>
                  {flatRateNetNew?.map((item, index) => (
                    <td key={index}>
                      <p>{convertT(t, item?.assets, true)}</p>
                    </td>
                  ))}
                  {flatRateRepurpose?.map((item, index) => (
                    <td key={index}>
                      <p>{convertT(t, item?.assets, true)}</p>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_edits', 'Edits')}</td>
                  {flatRateNetNew?.map((item, index) => (
                    <td key={index}>
                      <p>{convertT(t, item?.edits, true)}</p>
                    </td>
                  ))}
                  {flatRateRepurpose?.map((item, index) => (
                    <td key={index}>
                      <p>{convertT(t, item?.edits, true)}</p>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_talents', 'Talents')}</td>
                  {flatRateNetNew?.map((item, index) => (
                    <td key={index}>{convertT(t, item?.talents)}</td>
                  ))}
                  {flatRateRepurpose?.map((item, index) => (
                    <td key={index}>{convertT(t, item?.talents)}</td>
                  ))}
                </tr>
                <tr>
                  <td>
                    {t(
                      'exhibit_rate_card_header_expected_asset_delivery_date',
                      'Expected Asset Delivery Date'
                    )}
                  </td>
                  {flatRateNetNew?.map((item, index) => {
                    return <td key={index}>{convertT(t, item?.turnaroundTime, true)}</td>;
                  })}
                  {flatRateRepurpose?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>{convertT(t, item?.turnaroundTime, true)}</td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_remark', 'Remark')}</td>
                  {flatRateNetNew?.map((item) => (
                    <td key={item?.remark?.starlingKey}>
                      {convertT(t, item?.remark) ? convertT(t, item?.remark) : '-'}{' '}
                    </td>
                  ))}
                  {flatRateRepurpose?.map((item) => (
                    <td key={item?.remark?.starlingKey}> - </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <br />
        </>
      )}
      {showRegion && ((revShareNetNew?.length ?? 0) >= 1 || (revShareRepurpose?.length ?? 0) >= 1) && (
        <>
          <div className="packages-table-region">
            {t(`ttcx_region_${regionTextMap[region]}`) + ' (VCP) - Revenue Share Structure'}
          </div>
          <div>Base Rate</div>
        </>
      )}
      {((revShareNetNew?.length ?? 0) >= 1 || (revShareRepurpose?.length ?? 0) >= 1) && (
        <>
          <div className="packages-table-content">
            <table>
              <tbody>
                <tr>
                  <td>{t('exhibit_rate_card_header_service_type', 'Service Type')}</td>
                  {revShareNetNew.length > 0 && (
                    <td colSpan={revShareNetNew.length} key={'net-new'}>
                      {t('net_new', 'Net-New')}
                    </td>
                  )}
                  {revShareRepurpose.length > 0 && (
                    <td colSpan={revShareRepurpose.length} key={'repurpose'}>
                      {t('remix_project_title', 'Remix')}
                    </td>
                  )}
                </tr>
                <tr>
                  <td>Base Rate</td>
                  {/* <td>{t('exhibit_rate_card_header_rate_base', 'Rate base')}</td> */}
                  {revShareNetNew?.map((item) => (
                    <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
                  ))}
                  {revShareRepurpose?.map((item) => (
                    <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_price', 'Price')}</td>
                  {revShareNetNew?.map((item) => (
                    <td key={item?.basePayPerVideo}>
                      {`${(item?.basePayPerVideo || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: item?.currency || 'USD',
                        minimumFractionDigits: 0
                      })} ${item?.currency || 'USD'}`}
                    </td>
                  ))}
                  {revShareRepurpose?.map((item) => (
                    <td key={item?.basePayPerVideo}>
                      {`${(item?.basePayPerVideo || 0).toLocaleString('en-US', {
                        style: 'currency',
                        currency: item?.currency || 'USD',
                        minimumFractionDigits: 0
                      })} ${item?.currency || 'USD'}`}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_assets', 'Assets')}</td>
                  {revShareNetNew?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>
                      <p>{convertT(t, item?.assets, true)}</p>
                    </td>
                  ))}
                  {revShareRepurpose?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>
                      <p>{convertT(t, item?.assets, true)}</p>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_edits', 'Edits')}</td>
                  {revShareNetNew?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>
                      <p>{convertT(t, item?.edits, true)}</p>
                    </td>
                  ))}
                  {revShareRepurpose?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>
                      <p>{convertT(t, item?.edits, true)}</p>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_talents', 'Talents')}</td>
                  {revShareNetNew?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>{convertT(t, item?.talents)}</td>
                  ))}
                  {revShareRepurpose?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>{convertT(t, item?.talents)}</td>
                  ))}
                </tr>
                <tr>
                  <td>
                    {t(
                      'exhibit_rate_card_header_expected_asset_delivery_date',
                      'Expected Asset Delivery Date'
                    )}
                  </td>
                  {revShareNetNew?.map((item, index) => {
                    return <td key={index}>{convertT(t, item?.turnaroundTime, true)}</td>;
                  })}
                  {revShareRepurpose?.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={index}>{convertT(t, item?.turnaroundTime, true)}</td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_subscription_supported', 'Subscription supported')}</td>
                  {revShareNetNew?.map((item, idx) => (
                    <td key={`${idx}a`}>
                      {item?.supportSubscription ? t('table_yes', 'Yes') : t('table_no', 'No')}
                    </td>
                  ))}
                  {revShareRepurpose?.map((item, idx) => (
                    <td key={`${idx}b`}>{t('table_no', 'No')}</td>
                  ))}
                </tr>
                <tr>
                  <td>{t('exhibit_rate_card_header_remark', 'Remark')}</td>
                  {revShareNetNew?.map((item) => (
                    <td key={item?.remark?.starlingKey}>
                      {convertT(t, item?.remark) ? convertT(t, item?.remark) : '-'}{' '}
                    </td>
                  ))}
                  {revShareRepurpose?.map((item) => (
                    <td key={item?.remark?.starlingKey}> - </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <br />
        </>
      )}
      {showRegion && ((revShareNetNew?.length ?? 0) >= 1 || (revShareRepurpose?.length ?? 0) >= 1) && (
        <>
          <RevShareInformationTable />
          <br />
        </>
      )}
      {showRegion && addOns && (
        <div className="packages-table-region">
          {`${t(`ttcx_region_${regionTextMap[region]}`)} `}
          {t('exhibit_rate_card_header_add_on_service', 'Add-on Service')}
        </div>
      )}
      {showRegion &&
        addOns &&
        addOns.map((addOn) => (
          <>
            <div className="packages-table-addOn" key={'add-on'}>
              {t(AddOnTableTitleMap[addOn.type || AddOnTypeEnum.Undefined])}
            </div>
            <div className="packages-table-content">
              <table>
                <tbody>
                  <tr className="package-table-add-on-header">
                    <td />
                    <td key={'service-code'}>{t('exhibit_rate_card_header_service_code', 'Service Code')}</td>
                    <td key={'price'}>
                      {userRole === UserRoleEnum.Partner
                        ? t('exhibit_rate_card_header_price', 'Price')
                        : t('exhibit_rate_card_header_minimum_spend', 'Minimum Spend')}
                    </td>
                    <td key={'service-description'}>
                      {t('exhibit_rate_card_header_service_description', 'Service Description')}
                    </td>
                  </tr>
                  {addOn &&
                    (addOn.serieses || []).map((series) =>
                      series.addOns?.map((addOnItem, index) => (
                        <tr key={addOnItem?.code}>
                          {index === 0 && (
                            <td rowSpan={(series?.addOns || []).length}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    `<span>${convertT(t, series?.seriesNameV2)}</span>`
                                  )
                                }}
                              />
                            </td>
                          )}
                          <td className="package-table-add-on-info">{addOnItem?.code}</td>
                          {userRole === UserRoleEnum.Partner ? (
                            addOnItem?.officialPriceType === AddOnOfficialPriceTypeEnum.Percentage ? (
                              <td className="package-table-add-on-info">{`${(
                                (addOnItem?.price || 0) * 100
                              ).toFixed()}%`}</td>
                            ) : (
                              <td className="package-table-add-on-info">{`${formatCurrency(
                                addOnItem?.price,
                                addOnItem?.currency
                              )} ${addOnItem?.currency || 'USD'} `}</td>
                            )
                          ) : (
                            <td className="package-table-add-on-info">
                              {`${formatCurrency(addOnItem?.minSpend, addOnItem?.currency)} ${
                                addOnItem?.currency || 'USD'
                              } `}
                            </td>
                          )}
                          <td className="package-table-add-on-info">
                            {convertT(t, addOnItem?.rateCardDescriptionV2, true)}
                          </td>
                        </tr>
                      ))
                    )}
                </tbody>
              </table>
            </div>
            <br />
          </>
        ))}
      {showRegion &&
        addOns &&
        ((revShareNetNew?.length ?? 0) >= 1 || (revShareRepurpose?.length ?? 0) >= 1) && <RevShareAddOn />}
    </>
  );
}

function CLPTableSchema(props: {
  region: UserRegionEnum;
  showRegion?: boolean;
  userRole?: UserRoleEnum;
  netNewPackages?: ServicePackageRateCardInfo[];
  repurposePackages?: ServicePackageRateCardInfo[];
  addOns?: AddOnCategory[];
  regionTextMap: UserRegionEnumMap;
}) {
  const {
    region,
    showRegion = true,
    userRole,
    netNewPackages = [],
    repurposePackages = [],
    addOns,
    regionTextMap
  } = props;

  return (
    <>
      {showRegion && ((netNewPackages?.length ?? 0) >= 1 || (repurposePackages?.length ?? 0) >= 1) && (
        <div className="packages-table-region">{t(`ttcx_region_${regionTextMap[region]}`) + ' (CLP)'}</div>
      )}
      <div className="packages-table-content">
        <table>
          <tbody>
            {((netNewPackages?.length ?? 0) >= 1 || (repurposePackages?.length ?? 0) >= 1) && (
              <tr>
                <td>{t('exhibit_rate_card_header_service_type', 'Service Type')}</td>
                {netNewPackages.length > 0 && (
                  <td colSpan={netNewPackages.length} key={'net-new'}>
                    {t('net_new', 'Net-New')}
                  </td>
                )}
                {repurposePackages.length > 0 && (
                  <td colSpan={repurposePackages.length} key={'repurpose'}>
                    {t('remix_project_title', 'Remix')}
                  </td>
                )}
              </tr>
            )}
            <tr>
              <td>{t('exhibit_rate_card_header_rate_base', 'Rate base')}</td>
              {netNewPackages?.map((item) => (
                <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
              ))}
              {repurposePackages?.map((item) => (
                <td key={item?.rateBase?.fallbackText}>{convertT(t, item?.rateBase)}</td>
              ))}
            </tr>
            <tr>
              <td>{t('exhibit_rate_card_header_price', 'Price')}</td>
              {netNewPackages?.map((item) => (
                <td key={item?.price}>
                  {`${(item?.price || 0).toLocaleString('en-US', {
                    style: 'currency',
                    currency: item?.currency || 'USD',
                    minimumFractionDigits: 0
                  })} ${item?.currency || 'USD'}`}
                </td>
              ))}
              {repurposePackages?.map((item) => (
                <td key={item?.price}>
                  {`${(item?.price || 0).toLocaleString('en-US', {
                    style: 'currency',
                    currency: item?.currency || 'USD',
                    minimumFractionDigits: 0
                  })} ${item?.currency || 'USD'}`}
                </td>
              ))}
            </tr>
            <tr>
              <td>{t('exhibit_rate_card_header_assets', 'Assets')}</td>
              {netNewPackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.assets, true)}</p>
                </td>
              ))}
              {repurposePackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.assets, true)}</p>
                </td>
              ))}
            </tr>
            <tr>
              <td>{t('exhibit_rate_card_header_edits', 'Edits')}</td>
              {netNewPackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.edits, true)}</p>
                </td>
              ))}
              {repurposePackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>
                  <p>{convertT(t, item?.edits, true)}</p>
                </td>
              ))}
            </tr>
            <tr>
              <td>{t('exhibit_rate_card_header_talents', 'Talents')}</td>
              {netNewPackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>{convertT(t, item?.talents)}</td>
              ))}
              {repurposePackages?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <td key={index}>{convertT(t, item?.talents)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {showRegion && addOns && (
        <div className="packages-table-region">
          {`${t(`ttcx_region_${regionTextMap[region]}`)} `}
          {t('exhibit_rate_card_header_add_on_service', 'Add-on Service')}
        </div>
      )}
      {showRegion &&
        addOns &&
        addOns.map((addOn) => (
          <>
            <div className="packages-table-addOn" key={'add-on'}>
              {t(AddOnTableTitleMap[addOn.type || AddOnTypeEnum.Undefined])}
            </div>
            <div className="packages-table-content">
              <table>
                <tbody>
                  <tr className="package-table-add-on-header">
                    <td />
                    <td key={'service-code'}>{t('exhibit_rate_card_header_service_code', 'Service Code')}</td>
                    <td key={'price'}>
                      {userRole === UserRoleEnum.Partner
                        ? t('exhibit_rate_card_header_price', 'Price')
                        : t('exhibit_rate_card_header_minimum_spend', 'Minimum Spend')}
                    </td>
                    <td key={'service-description'}>
                      {t('exhibit_rate_card_header_service_description', 'Service Description')}
                    </td>
                  </tr>
                  {addOn &&
                    (addOn.serieses || []).map((series) =>
                      series.addOns?.map((addOnItem, index) => (
                        <tr key={addOnItem?.code}>
                          {index === 0 && (
                            <td rowSpan={(series?.addOns || []).length}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    `<span>${convertT(t, series?.seriesNameV2)}</span>`
                                  )
                                }}
                              />
                            </td>
                          )}
                          <td className="package-table-add-on-info">{addOnItem?.code}</td>
                          {userRole === UserRoleEnum.Partner ? (
                            addOnItem?.officialPriceType === AddOnOfficialPriceTypeEnum.Percentage ? (
                              <td className="package-table-add-on-info">{`${(
                                (addOnItem?.price || 0) * 100
                              ).toFixed()}%`}</td>
                            ) : (
                              <td className="package-table-add-on-info">{`${formatCurrency(
                                addOnItem?.price,
                                addOnItem?.currency
                              )} ${addOnItem?.currency || 'USD'} `}</td>
                            )
                          ) : (
                            <td className="package-table-add-on-info">
                              {`${formatCurrency(addOnItem?.minSpend, addOnItem?.currency)} ${
                                addOnItem?.currency || 'USD'
                              } `}
                            </td>
                          )}
                          <td className="package-table-add-on-info">
                            {convertT(t, addOnItem?.rateCardDescriptionV2, true)}
                          </td>
                        </tr>
                      ))
                    )}
                </tbody>
              </table>
            </div>
          </>
        ))}
    </>
  );
}

function PackageTableSchema(props: {
  region: UserRegionEnum;
  showRegion?: boolean;
  userRole?: UserRoleEnum;
  netNewPackages?: ServicePackageRateCardInfo[];
  repurposePackages?: ServicePackageRateCardInfo[];
  addOns?: AddOnCategory[];
  regionTextMap: UserRegionEnumMap;
  isCLP?: boolean;
}) {
  const {
    region,
    showRegion = true,
    userRole,
    netNewPackages = [],
    repurposePackages = [],
    addOns,
    regionTextMap,
    isCLP = false
  } = props;

  return (
    <div className="packages-table">
      {isCLP ? (
        <CLPTableSchema
          region={region}
          showRegion={showRegion}
          userRole={userRole}
          netNewPackages={netNewPackages}
          repurposePackages={repurposePackages}
          addOns={addOns}
          regionTextMap={regionTextMap}
        />
      ) : (
        <VCPTableSchema
          region={region}
          showRegion={showRegion}
          userRole={userRole}
          netNewPackages={netNewPackages}
          repurposePackages={repurposePackages}
          addOns={addOns}
          regionTextMap={regionTextMap}
        />
      )}
    </div>
  );
}

export default PackageTableSchema;
